import {HttpInterceptorFn} from '@angular/common/http';
import {inject} from "@angular/core";
import {AuthService} from "./services/auth.service";
import {environment} from "./environments/environment";

/**
 * Attaches the Cognito access token, but ONLY to requests aimed at this app's own API.
 * Attaching it unconditionally would hand a valid admin bearer token to any other host the app
 * ever requests -- an image CDN, an analytics endpoint, a redirect that leaves the API origin.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isApiRequest(req.url)) {
    return next(req);
  }

  const authService = inject(AuthService);

  if (authService.token === undefined) {
    return next(req);
  }

  return next(req.clone({
    setHeaders: { Authorization: `Bearer ${authService.token}` },
  }));
};

/**
 * Compares parsed origins rather than doing `startsWith` on the raw string, so a host that
 * merely begins with the API URL (`https://<api-host>.attacker.example`) does not match.
 */
const isApiRequest = (url: string): boolean => {
  try {
    const apiUrl = new URL(environment.apiUrl);
    const requestUrl = new URL(url);

    return requestUrl.origin === apiUrl.origin && requestUrl.pathname.startsWith(apiUrl.pathname);
  } catch {
    // A relative URL -- never the API, whose base URL is absolute -- or a malformed one.
    return false;
  }
};
